import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./App.css";

// Pages
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { DashboardPage } from "./pages/DashboardPage";
import { CalendarPage } from "./pages/CalendarPage";
import { ComposerPage } from "./pages/ComposerPage";
import { MediaLibraryPage } from "./pages/MediaLibraryPage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { CompetitorAdvisorPage } from "./pages/CompetitorAdvisorPage";
import { AccountsPage } from "./pages/AccountsPage";
import { SubscriptionPage } from "./pages/SubscriptionPage";
import { SubscriptionSuccessPage } from "./pages/SubscriptionSuccessPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { AuthCallback } from "./pages/AuthCallback";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
// New 4-pillar pages
import { EditorPage } from "./pages/EditorPage";
import { IdeasPage } from "./pages/IdeasPage";
import { BrandsPage } from "./pages/BrandsPage";
import { TrendsPage } from "./pages/TrendsPage";
import { CollectionsPage } from "./pages/CollectionsPage";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Auth Context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = async () => {
    const storedToken = localStorage.getItem("token");
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setUser(response.data);
      setToken(storedToken);
    } catch (error) {
      localStorage.removeItem("token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData, authToken) => {
    localStorage.setItem("token", authToken);
    setToken(authToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        });
        setUser(response.data);
      } catch (error) {
        console.error("Error refreshing user:", error);
      }
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return { user, loading, token, login, logout, checkAuth, refreshUser };
};

// Protected Route Component
const ProtectedRoute = ({ children, user, loading }) => {
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Onboarding Check Component - Redirects to onboarding if not completed
const OnboardingCheck = ({ children, user, loading, token, refreshUser }) => {
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if onboarding is completed
  if (user.onboarding_completed === false) {
    return <OnboardingPage user={user} token={token} onComplete={refreshUser} />;
  }

  return children;
};

// App Router Component
function AppRouter() {
  const location = useLocation();
  const auth = useAuth();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback login={auth.login} />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={
        auth.user ? <Navigate to="/dashboard" replace /> : <LoginPage login={auth.login} />
      } />
      <Route path="/register" element={
        auth.user ? <Navigate to="/dashboard" replace /> : <RegisterPage login={auth.login} />
      } />
      <Route path="/onboarding" element={
        <ProtectedRoute user={auth.user} loading={auth.loading}>
          <OnboardingPage user={auth.user} token={auth.token} onComplete={auth.refreshUser} />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <DashboardPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      <Route path="/calendar" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <CalendarPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      <Route path="/composer" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <ComposerPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      <Route path="/media" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <MediaLibraryPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      <Route path="/analytics" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <AnalyticsPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      <Route path="/advisor" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <CompetitorAdvisorPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      <Route path="/accounts" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <AccountsPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      <Route path="/settings" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <SettingsPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      <Route path="/subscription" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <SubscriptionPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      <Route path="/subscription/success" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <SubscriptionSuccessPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      {/* New 4-pillar navigation routes */}
      <Route path="/editor" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <EditorPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      <Route path="/ideas" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <IdeasPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      <Route path="/ideas/trends" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <TrendsPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      <Route path="/ideas/collections" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <CollectionsPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      <Route path="/brands" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <BrandsPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      <Route path="/brands/*" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <BrandsPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      <Route path="/templates" element={
        <OnboardingCheck user={auth.user} loading={auth.loading} token={auth.token} refreshUser={auth.refreshUser}>
          <EditorPage user={auth.user} logout={auth.logout} token={auth.token} />
        </OnboardingCheck>
      } />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/stratia/terms-of-service" element={<TermsPage />} />
      <Route path="/stratia/privacy-policy" element={<PrivacyPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
